import { Injectable, inject } from '@angular/core';
import { tap, catchError, take, finalize } from 'rxjs/operators';
import { State, Action, StateContext, Store } from '@ngxs/store';

import { VoiceProduct } from '@shared/models/voice-product.model';
import { HardwareProduct } from '@shared/models/hardware-product.model';
import { NotificationService } from '@core/services/notification.service';

import { MbbProduct } from '@sales/modules/mbb/models/mbb-product.model';
import {
  AddVoiceProductsAction,
  AddMbbProductsAction,
  OrderCaptureResetAction,
  AddProductAction,
  AddHardwareProductsAction,
  AddMobileProductsAction,
  AddReturnBoxAction,
  AddCustomerServiceProductsAction,
  AddExternalShoppingCartAction,
  CreateAuthorizedShoppingCartAction,
  AuthorizedShoppingCartAction,
  PlaceOrderResetAction,
  AddTradeInProductAction,
  AddVendorSubsidyVoucherAction,
  GenerateContractDataAction,
  PlaceOrderAction,
  CreateOrderRequestCompletedAction,
  AddOrderItemAction,
} from './order-capture.actions';
import { ShoppingCartResponse, AuthorizedShoppingCart } from '../../models/order-capture.model';
import { ShoppingCartService } from '../../services/shopping-cart.service';
import { OrderCaptureHelperService } from '../../services/order-capture-helper.service';
import {
  PlaceOrderErrorAction,
  PlaceOrderFailedAction,
  SetOrderCaptureIdAction,
  SetOrderServiceIdAction,
} from '../order/order.actions';
import { MobilePhoneProduct } from '../../models/mobilephone-product';
import { MobilePaymentType } from '../../enums/mobile-payment-type';
import { CustomerProductSelectors } from '../customer-product/customer-product.selectors';
import { CustomerProductMobilePhoneSelectors } from '../customer-product-mobilephone/customer-product-mobilephone.selectors';
import {
  CustomerEquipmentUpgradeAgreement,
  MobileReturnMethod,
} from '../../modules/mobilephone/models/equipment-upgrade-agreement.model';
import { MobilePhoneProductHelperService } from '../../services/mobile-phone-product-helper.service';
import { CustomerServiceProductSelectors } from '../customer-service-product/customer-service-product.selectors';
import { CustomerServiceProduct } from '@shared/models/customer-service-product';
import {
  ExistingCustomerFoundAction,
  DownpaymentFailedAction,
  DownpaymentSuccessAction,
  ExistingCustomerCreditCheckFailedAction,
  ExistingCustomerCreditCheckSuccessAction,
  NewCustomerCreditCheckFailedAction,
  NewCustomerCreditCheckSuccessAction,
  SetCustomerCreditCheckErrorAction,
} from '@sales/modules/details/states/customer.actions';
import { ProblemCustomerCreditCheck, ProblemExistingCustomerSsn } from '@app/core/http-interceptors/http-error.interceptor';
import { AuthorizeResult } from '@core/models/initialize-response.model';
import { TradeInSelectors } from '../customer-product-trade-in/customer-product-trade-in.selectors';
import { AcceptTradeInQuoteResponse, VendorSubsidyResponse } from '@app/sales/models/trade-in-product.model';
import { MobilBytteSelectors } from '@app/sales/modules/mobilephone/states/mobilbytte/mobilbytte.selectors';
import { PlaceItemsResponse } from '@app/sales/models/place-items-response.model';
import { HttpStatusCode } from '@angular/common/http';
import { Router } from '@angular/router';
import { diegoRoutes } from '@app/core/enums/orderflow-paths';
import { OrderService } from '@app/sales/services/order.service';
import { OrderItem, ProductOrderRequest } from '@app/shared/models/order.model';
import { OrderSelectors } from '../order/order.selectors';
import { ShoppingCartSelectors } from '../shopping-cart/shopping-cart.selectors';
import { ContractTypeData } from '@app/sales/models/contract.model';

enum Messages {
  CreateOrderFail = 'Feil ved opprettelse av ordre.',
  PlaceOrderFailed = 'Plassering av ordre var mislykket!',
  PlaceOrderSuccess = 'Plassering av ordre var vellykket.',
  GenerateContractError = 'Opprettelse av kontrakt feilet.',
}

export interface OrderCaptureStateModel {
  isLoadingOrderPlacement: boolean;
  shoppingCart: ShoppingCartResponse;
  createOrderRequest: ProductOrderRequest;
}

@State<OrderCaptureStateModel>({
  name: 'orderCapture',
  defaults: {
    isLoadingOrderPlacement: false,
    shoppingCart: null,
    createOrderRequest: { ...new ProductOrderRequest() },
  },
})
@Injectable()
export class OrderCaptureState {
  private store = inject(Store);
  private shoppingCartService = inject(ShoppingCartService);
  private orderService = inject(OrderService);
  private orderCaptureHelperService = inject(OrderCaptureHelperService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  @Action(OrderCaptureResetAction, { cancelUncompleted: true })
  public cartReset({ setState }: StateContext<OrderCaptureStateModel>) {
    setState({
      isLoadingOrderPlacement: false,
      shoppingCart: null,
      createOrderRequest: { ...new ProductOrderRequest() },
    });
  }

  @Action(PlaceOrderResetAction, { cancelUncompleted: true })
  public placeOrderReset({ setState, getState }: StateContext<OrderCaptureStateModel>) {
    setState({
      isLoadingOrderPlacement: false,
      shoppingCart: {
        externalShoppingCartId: getState().shoppingCart?.externalShoppingCartId,
        items: [],
      } as ShoppingCartResponse,
      createOrderRequest: { ...new ProductOrderRequest() },
    });
  }

  @Action(CreateAuthorizedShoppingCartAction)
  public createAuthorizedShoppingCart(
    { setState, dispatch }: StateContext<OrderCaptureStateModel>,
    { request }: CreateAuthorizedShoppingCartAction
  ) {
    return this.shoppingCartService.createShoppingCart(request).pipe(
      tap(
        (authorizedShoppingCart: AuthorizedShoppingCart) => {
          let { externalShoppingCartId, orderServiceId, ...authorizeResultObject } = { ...authorizedShoppingCart };
          let authorizeResult = { ...authorizeResultObject } as AuthorizeResult;
          setState({
            isLoadingOrderPlacement: false,
            shoppingCart: {
              externalShoppingCartId: externalShoppingCartId,
              items: [],
            } as ShoppingCartResponse,
            createOrderRequest: { ...new ProductOrderRequest() },
          });

          if (!authorizedShoppingCart.creditCheckedEntity && !authorizedShoppingCart.approved) {
            dispatch(new SetCustomerCreditCheckErrorAction(authorizedShoppingCart.reason));
          }

          if (request.customerNo) {
            dispatch([
              new ExistingCustomerCreditCheckSuccessAction(authorizeResult, request.authorizeForDownPayments),
              new SetOrderServiceIdAction(+orderServiceId),
            ]);
          } else {
            dispatch([
              new NewCustomerCreditCheckSuccessAction(authorizeResult, request.ssn),
              new SetOrderServiceIdAction(+authorizedShoppingCart.orderServiceId),
            ]);
          }
        },
        error => {
          if (error instanceof ProblemExistingCustomerSsn) {
            dispatch(
              new ExistingCustomerFoundAction(
                error.error?.properties?.customerId,
                request.firstName,
                request.lastName,
                request.ssn,
                request.authorizeForDownPayments
              )
            );
          } else if (error instanceof ProblemCustomerCreditCheck) {
            dispatch(new SetCustomerCreditCheckErrorAction(error.error?.detail));
          } else {
            request.customerNo
              ? dispatch(new ExistingCustomerCreditCheckFailedAction(error))
              : dispatch(new NewCustomerCreditCheckFailedAction(error));
          }
        }
      )
    );
  }

  @Action(AuthorizedShoppingCartAction)
  public authorizedShoppingCart(
    { dispatch, getState }: StateContext<OrderCaptureStateModel>,
    { request }: AuthorizedShoppingCartAction
  ) {
    let state = getState();
    if (state.shoppingCart?.externalShoppingCartId) {
      return this.shoppingCartService.authorizeShoppingCart(state.shoppingCart.externalShoppingCartId, request).pipe(
        tap((authorizedShoppingCart: AuthorizedShoppingCart) => {
          // eslint-disable-next-line unused-imports/no-unused-vars
          let { externalShoppingCartId, orderServiceId, ...authorizeResultObject } = { ...authorizedShoppingCart };
          let authorizeResult = { ...authorizeResultObject } as AuthorizeResult;
          dispatch(new DownpaymentSuccessAction(authorizeResult));
        }),
        catchError(error => dispatch(new DownpaymentFailedAction(error)))
      );
    }
  }

  @Action(AddExternalShoppingCartAction)
  public addExternalShoppingCart(
    { setState }: StateContext<OrderCaptureStateModel>,
    { externalShoppingCartId }: AddExternalShoppingCartAction
  ) {
    setState({
      isLoadingOrderPlacement: false,
      shoppingCart: { externalShoppingCartId, items: [] } as ShoppingCartResponse,
      createOrderRequest: { ...new ProductOrderRequest() },
    });
  }

  @Action(PlaceOrderAction)
  public async PlaceOrder({ dispatch, getState, patchState }: StateContext<OrderCaptureStateModel>) {
    const { createOrderRequest, shoppingCart } = getState();
    const productOrderRequest = this.orderCaptureHelperService.getProductOrderRequest(shoppingCart.externalShoppingCartId);
    patchState({
      isLoadingOrderPlacement: true,
      createOrderRequest: { ...createOrderRequest, ...productOrderRequest },
    });

    await dispatch(new AddVoiceProductsAction());
    await dispatch(new AddMbbProductsAction());
    await dispatch(new AddHardwareProductsAction());
    await dispatch(new AddMobileProductsAction());
    await dispatch(new AddReturnBoxAction());
    await dispatch(new AddCustomerServiceProductsAction());
    await dispatch(new AddTradeInProductAction());
    await dispatch(new AddVendorSubsidyVoucherAction());
    await dispatch(new GenerateContractDataAction());

    dispatch(new CreateOrderRequestCompletedAction());
  }

  @Action(AddVoiceProductsAction)
  public async addVoiceProducts({ dispatch }: StateContext<OrderCaptureStateModel>) {
    const allNewAndChangedVoiceProducts = this.store.selectSnapshot(CustomerProductSelectors.newAndChangedVoiceProducts);
    const newAndChangedVoiceProductsToUpdate = allNewAndChangedVoiceProducts.filter(
      p =>
        (!p.mobileProductId && !p.accessoryProductId) ||
        ((p.mobileProductId || p.accessoryProductId) && p.isExistingSubscription)
    );

    for (const product of newAndChangedVoiceProductsToUpdate) {
      const offeringId = product.isExistingSubscription
        ? product.offering?.id
          ? product.offering.id
          : product.assignedSubscription?.subscriptionNo || product.subscription.id
        : product.offering.id;
      const connectedMobileProduct = !!product.mobileProductId
        ? this.store
            .selectSnapshot<MobilePhoneProduct[]>(CustomerProductMobilePhoneSelectors.mobilePhoneProducts)
            .find(mobileProduct => mobileProduct.id === product.mobileProductId)
        : null;

      await dispatch(
        new AddProductAction(offeringId.toString(), {
          ...product,
          imei: connectedMobileProduct ? connectedMobileProduct.imei : null,
        } as VoiceProduct)
      ).toPromise();
    }
  }

  @Action(AddMbbProductsAction)
  public async addMbbProducts({ dispatch }: StateContext<OrderCaptureStateModel>) {
    const mbbProducts = this.store.selectSnapshot<MbbProduct[]>(state => state.customerProductsMbb.products);
    const products = mbbProducts.filter(x => !x.isExistingSubscription || (x.mbbSubscription && x.mbbSubscription.id));

    for (const product of products) {
      await dispatch(
        new AddProductAction(
          product.selectedBundle ? product.selectedBundle.offeringId : product.mbbSubscription.id,
          product
        )
      ).toPromise();
    }
  }

  @Action(AddHardwareProductsAction)
  public async addHardwareProducts({ dispatch }: StateContext<OrderCaptureStateModel>) {
    const hardwareProducts = this.store.selectSnapshot<HardwareProduct[]>(state => state.customerProductsAccessory.products);
    for (const product of hardwareProducts) {
      let offeringId: string;

      if (product.selectedSubscription) {
        const { subscriptionOfferingId, hardwareOfferingId } = product.selectedSubscription;

        offeringId = product.equipment.isManualRegistration ? subscriptionOfferingId : hardwareOfferingId;
      } else {
        offeringId = product.equipment.offeringId;
      }

      await dispatch(new AddProductAction(offeringId, product)).toPromise();
    }
  }

  @Action(AddMobileProductsAction)
  public async addMobileProductsAction({ dispatch }: StateContext<OrderCaptureStateModel>) {
    const voiceProducts = this.store.selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts);

    const allMobileProducts = this.store.selectSnapshot<MobilePhoneProduct[]>(
      CustomerProductMobilePhoneSelectors.mobilePhoneProducts
    );
    const mobileProducts = allMobileProducts.filter(p => {
      const connectedVoiceProduct = this.store
        .selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts)
        .find(voiceProduct => voiceProduct.mobileProductId === p.id);

      return (
        [MobilePaymentType.EquipmentUpgrade, MobilePaymentType.PayNowWithSubscription].includes(p.paymentType) ||
        p.sendInPost ||
        (p.isManualRegistration && connectedVoiceProduct && !connectedVoiceProduct.isExistingSubscription) ||
        (p.paymentType === MobilePaymentType.PayNowWithoutSubscription && !p.sendInPost)
      );
    });

    for (const product of mobileProducts) {
      if (
        product.paymentType === MobilePaymentType.PayNowWithoutSubscription &&
        !product.sendInPost &&
        product.insuranceOption
      ) {
        await dispatch(new AddProductAction(product.insuranceOption.offeringId, product)).toPromise();
      } else if ([MobilePaymentType.PayNowWithSubscription].includes(product.paymentType) && !product.isManualRegistration) {
        const bundleId = MobilePhoneProductHelperService.getBundleIdForSelectedMobileAndVoiceProduct(product, voiceProducts);
        if (bundleId) {
          await dispatch(new AddProductAction(bundleId, product)).toPromise();
        }
      } else {
        if (product.isExternalCellPhone) {
          const connectedVoiceProduct = voiceProducts.find(voiceProduct => voiceProduct.mobileProductId === product.id);
          await dispatch(new AddProductAction(connectedVoiceProduct?.offering?.id, product)).toPromise();
        } else if (product.paymentType === MobilePaymentType.EquipmentUpgrade) {
          if (product.mobilBytteOffering) {
            await dispatch(new AddProductAction(product.mobilBytteOffering, product)).toPromise();
          }
        } else {
          await dispatch(new AddProductAction(product.mobilePhoneVariant?.offeringId, product)).toPromise();
        }
      }
    }
  }

  @Action(AddReturnBoxAction)
  public async addReturnBoxAction({ dispatch }: StateContext<OrderCaptureStateModel>) {
    const equipmentUpgradeUpgradeMobileProducts = this.store
      .selectSnapshot<MobilePhoneProduct[]>(CustomerProductMobilePhoneSelectors.mobilePhoneProducts)
      .filter(p => p.paymentType === MobilePaymentType.EquipmentUpgrade && p.existingAgreementId);

    const existingEquipmentUpgradeAgreements = this.store.selectSnapshot<CustomerEquipmentUpgradeAgreement[]>(
      MobilBytteSelectors.existingEquipmentUpgradeAgreements
    );

    for (const mobileProduct of equipmentUpgradeUpgradeMobileProducts) {
      const existingAgreement = existingEquipmentUpgradeAgreements.find(
        a => a.agreementId === mobileProduct.existingAgreementId
      );

      if (existingAgreement.mobileReturnMethod === MobileReturnMethod.ReturnPackage) {
        const sendReturnBoxRequest = this.orderCaptureHelperService.getReturnBoxRequest();
        const orderItem = {
          productOffering: {
            id: mobileProduct.mobilePhoneVariant.offeringId,
          },
          product: {
            captureRequest: sendReturnBoxRequest,
          },
        } as OrderItem;

        dispatch(new AddOrderItemAction(orderItem));
      }
    }

    const hasTradeIn = this.store.selectSnapshot<boolean>(TradeInSelectors.hasAcceptedTradeInVoucher);
    if (hasTradeIn) {
      const sendReturnBoxRequest = this.orderCaptureHelperService.getReturnBoxRequest();
      const orderItem = {
        productOffering: {
          id: null,
        },
        product: {
          captureRequest: sendReturnBoxRequest,
        },
      } as OrderItem;

      dispatch(new AddOrderItemAction(orderItem));
    }
  }

  @Action(AddCustomerServiceProductsAction)
  public async addCustomerServiceProducts({ dispatch }: StateContext<OrderCaptureStateModel>) {
    const newCustomerServiceProducts = this.store.selectSnapshot<CustomerServiceProduct[]>(
      CustomerServiceProductSelectors.getCustomerServiceProductsAdded
    );

    for (const newCustomerServiceProduct of newCustomerServiceProducts) {
      const customerServiceProductRequest =
        this.orderCaptureHelperService.getCustomerServiceProductRequest(newCustomerServiceProduct);

      const { offeringId, name, description } = newCustomerServiceProduct.customerServiceOffering;
      const orderItem = {
        productOffering: {
          id: offeringId,
          name,
        },
        product: {
          name,
          description,
          captureRequest: customerServiceProductRequest,
        },
      } as OrderItem;

      dispatch(new AddOrderItemAction(orderItem));
    }
  }

  @Action(AddTradeInProductAction)
  public async addTradeInProduct({ dispatch }: StateContext<OrderCaptureStateModel>) {
    const tradeInVoucherId = this.store.selectSnapshot<AcceptTradeInQuoteResponse>(TradeInSelectors.quote)?.voucherId;

    const hasAcceptedTradeInVoucher = this.store.selectSnapshot<boolean>(TradeInSelectors.hasAcceptedTradeInVoucher);
    if (hasAcceptedTradeInVoucher && !!tradeInVoucherId) {
      const deviceDisplayName = this.store.selectSnapshot<string>(TradeInSelectors.deviceDisplayName);
      const tradeInProductRequest = this.orderCaptureHelperService.getTradeInProductRequest();
      const orderItem = {
        productOffering: {
          id: tradeInVoucherId,
          name: deviceDisplayName,
        },
        product: {
          name: deviceDisplayName,
          captureRequest: tradeInProductRequest,
        },
      } as OrderItem;

      dispatch(new AddOrderItemAction(orderItem));
    }
  }

  @Action(AddVendorSubsidyVoucherAction)
  public async addVendorSubsidyVoucher({ dispatch }: StateContext<OrderCaptureStateModel>) {
    const vendorSubsidyVoucherRequest = this.orderCaptureHelperService.getVendorSubsidyVoucherRequest();
    const vendorSubsidy = this.store.selectSnapshot<VendorSubsidyResponse>(TradeInSelectors.vendorSubsidy);
    const vendorSubsidyVoucherId = vendorSubsidy?.voucherId;

    const hasAcceptedTradeInVoucher = this.store.selectSnapshot<boolean>(TradeInSelectors.hasAcceptedTradeInVoucher);
    if (hasAcceptedTradeInVoucher && !!vendorSubsidyVoucherId) {
      const { title, description } = vendorSubsidy.displayInfo;
      const orderItem = {
        productOffering: {
          id: vendorSubsidyVoucherId,
          name: title,
        },
        product: {
          name: title,
          description,
          captureRequest: vendorSubsidyVoucherRequest,
        },
      } as OrderItem;

      dispatch(new AddOrderItemAction(orderItem));
    }
  }

  @Action(AddProductAction)
  public addProduct({ dispatch }: StateContext<OrderCaptureStateModel>, { offeringId, product }: AddProductAction) {
    const name = this.orderCaptureHelperService.getProductName(product);
    const description = this.orderCaptureHelperService.getProductDescription(product);
    const requests = this.orderCaptureHelperService.getCaptureRequests(product);
    if (requests.length === 0) {
      throw new Error('No capture request on product');
    }

    requests.forEach(request => {
      const orderItem = {
        productOffering: {
          id: offeringId,
          name,
        },
        product: {
          name,
          description,
          captureRequest: request,
        },
      } as OrderItem;

      dispatch(new AddOrderItemAction(orderItem));
    });
  }

  @Action(AddOrderItemAction)
  public addOrderItem({ getState, patchState }: StateContext<OrderCaptureStateModel>, { orderItem }: AddOrderItemAction) {
    const state = getState();

    // Premature implementation of properties to prevent bad requests. Not in use yet.
    orderItem.quantity = 1;
    orderItem.product.isBundle = false;

    patchState({
      createOrderRequest: {
        ...state.createOrderRequest,
        orderItems: [...state.createOrderRequest.orderItems, orderItem],
      },
    });
  }

  @Action(GenerateContractDataAction)
  public generateContractData({ getState, patchState }: StateContext<OrderCaptureStateModel>) {
    const { createOrderRequest } = getState();
    const contractSigningMethod = this.store.selectSnapshot<ContractTypeData>(OrderSelectors.selectedContractSigningMethod);
    const isContractRequired = this.store.selectSnapshot<boolean>(ShoppingCartSelectors.isContractRequired);

    patchState({
      createOrderRequest: {
        ...createOrderRequest,
        contract: isContractRequired ? contractSigningMethod : null,
      },
    });
  }

  @Action(CreateOrderRequestCompletedAction, { cancelUncompleted: true })
  public createOrderRequestCompleted({ getState, patchState, dispatch }: StateContext<OrderCaptureStateModel>) {
    const { createOrderRequest } = getState();
    return this.orderService.createOrder(createOrderRequest).pipe(
      take(1),
      tap((response: PlaceItemsResponse) => {
        const { success, orderId, orderServiceId, errors } = response;
        dispatch(new SetOrderServiceIdAction(+orderServiceId));
        dispatch(new SetOrderCaptureIdAction(orderId));

        if (success) {
          this.router.navigate([diegoRoutes.orderHistory, orderServiceId]);
          this.notificationService.success(Messages.PlaceOrderSuccess);
        } else {
          dispatch(new PlaceOrderFailedAction(errors));
        }
      }),
      finalize(() =>
        patchState({
          isLoadingOrderPlacement: false,
        })
      ),
      catchError(errorResponse => {
        if (!!errorResponse?.error && errorResponse.status === HttpStatusCode.BadRequest) {
          this.notificationService.error(errorResponse?.error);
        }

        return dispatch(new PlaceOrderErrorAction([Messages.PlaceOrderFailed]));
      })
    );
  }
}
